<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { isRecord, toNullableBoolean, toNullableNumber, toNullableString } from '@/utils/coerce';
import { getRiskLevelDisplay } from '@/utils/kyc';
import type { KycCategory } from '@/types/kyc';

interface LocationCityRecord {
  city: string | null;
  state: string | null;
  cityCode: string | null;
  isMainForEntity: boolean;
  isBorderCity: boolean;
  isPortCity: boolean;
  levelValue: number | null;
  ruleId: string | null;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();

const unwrapRecord = (item: Record<string, unknown>): Record<string, unknown> => {
  if (isRecord(item._custom) && isRecord(item._custom.value)) {
    return item._custom.value;
  }
  return item;
};

const normalizeRecord = (item: Record<string, unknown>): LocationCityRecord => ({
  city: toNullableString(item.city),
  state: toNullableString(item.state),
  cityCode: toNullableString(item.city_code),
  isMainForEntity: toNullableBoolean(item.is_main_for_entity) ?? false,
  isBorderCity: toNullableBoolean(item.is_border_city) ?? false,
  isPortCity: toNullableBoolean(item.is_port_city) ?? false,
  levelValue: toNullableNumber(item.level_value),
  ruleId: toNullableString(item.rule_id),
});

const locations = computed<LocationCityRecord[]>(() => {
  const raw = props.category.data_json;
  const items = Array.isArray(raw)
    ? raw
    : isRecord(raw)
      ? raw.addresses ?? raw.items
      : [];

  if (!Array.isArray(items)) return [];

  return items
    .filter(isRecord)
    .map(unwrapRecord)
    .filter(isRecord)
    .map(normalizeRecord)
    .sort((left, right) => {
      if (left.isMainForEntity !== right.isMainForEntity) return left.isMainForEntity ? -1 : 1;
      return (right.levelValue ?? -1) - (left.levelValue ?? -1);
    });
});

const mainLocation = computed(() => locations.value.find((location) => location.isMainForEntity) ?? locations.value[0] ?? null);

const otherLocations = computed(() => {
  if (!mainLocation.value) return [];
  return locations.value.filter((location) => location !== mainLocation.value);
});

const formatCityLabel = (location: LocationCityRecord): string => {
  const city = location.city ?? 'Cidade não informada';
  return location.state ? `${city}, ${location.state}` : city;
};

const getLocationTypeLabels = (location: LocationCityRecord): string[] => {
  const labels: string[] = [];
  if (location.isBorderCity) labels.push('Fronteira');
  if (location.isPortCity) labels.push('Portuária');
  return labels;
};

const getRiskTagAttrs = (levelValue: number | null) => {
  const display = getRiskLevelDisplay(levelValue);
  return {
    value: display.label,
    style: {
      backgroundColor: display.hexBgColor,
      color: display.hexColor,
    },
    class: [display.borderClass, 'border'],
  };
};

const hasAnyFlag = (location: LocationCityRecord): boolean => getLocationTypeLabels(location).length > 0;
</script>

<template>
  <div class="mt-3 space-y-4">
    <div
      v-if="mainLocation"
      class="rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 via-white to-white p-4 shadow-sm"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="flex items-start gap-3">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
            <BaseIcon class="text-2xl">location_city</BaseIcon>
          </div>
          <div>
            <div class="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
              Localização principal
            </div>
            <div class="mt-1 text-lg font-semibold text-slate-900">
              {{ formatCityLabel(mainLocation) }}
            </div>
            <div v-if="mainLocation.cityCode" class="text-sm text-slate-500">
              Código do município: <span class="font-mono">{{ mainLocation.cityCode }}</span>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <Tag v-bind="getRiskTagAttrs(mainLocation.levelValue)" />
          <Tag severity="info" value="Principal" />
        </div>
      </div>

      <div class="mt-3 flex flex-wrap gap-2">
        <Tag
          v-for="label in getLocationTypeLabels(mainLocation)"
          :key="label"
          :value="label"
          severity="warning"
        />
        <span v-if="!hasAnyFlag(mainLocation)" class="text-sm text-slate-500">
          Sem classificação especial para esta cidade.
        </span>
      </div>
    </div>

    <div
      v-if="locations.length"
      class="max-h-[min(24vh,36rem)] overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 bg-white overscroll-y-contain"
    >
      <DataTable
        :value="locations"
        responsiveLayout="scroll"
        stripedRows
        class="p-datatable-sm text-sm"
      >
        <Column header="Risco">
          <template #body="{ data }: { data: LocationCityRecord }">
            <Tag v-bind="getRiskTagAttrs(data.levelValue)" />
          </template>
        </Column>

        <Column header="Cidade">
          <template #body="{ data }: { data: LocationCityRecord }">
            <div class="font-medium text-slate-900">{{ formatCityLabel(data) }}</div>
            <div v-if="data.cityCode" class="text-xs text-slate-500">
              Código <span class="font-mono">{{ data.cityCode }}</span>
            </div>
          </template>
        </Column>

        <Column header="Perfil">
          <template #body="{ data }: { data: LocationCityRecord }">
            <Tag :value="data.isMainForEntity ? 'Principal' : 'Adicional'" :severity="data.isMainForEntity ? 'info' : 'secondary'" />
          </template>
        </Column>

        <Column header="Classificação">
          <template #body="{ data }: { data: LocationCityRecord }">
            <div v-if="getLocationTypeLabels(data).length" class="flex flex-wrap gap-1.5">
              <Tag
                v-for="label in getLocationTypeLabels(data)"
                :key="label"
                :value="label"
                severity="warning"
              />
            </div>
            <span v-else class="text-xs text-slate-500">Sem classificação especial</span>
          </template>
        </Column>
      </DataTable>
    </div>

    <div v-else class="text-sm italic text-slate-500">
      Nenhum dado de localização por cidade encontrado.
    </div>

    <div
      v-if="otherLocations.length"
      class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500"
    >
      Além da localização principal, há {{ otherLocations.length }}
      {{ otherLocations.length === 1 ? 'cidade adicional' : 'cidades adicionais' }} considerada{{ otherLocations.length === 1 ? '' : 's' }} nesta verificação.
    </div>
  </div>
</template>
